import React, { useMemo, useCallback } from "react";
import { ChevronDown, ArrowDown, ArrowUp } from "lucide-react";
import BestBadge from "./BestBadge";
import type { Instance } from "../types";
import { useFilterStore, type ColumnKey } from "../../../store/filterStore";
import { useInstanceStore } from "../../../store/useInstanceStore";

interface InstanceRowProps {
  instance: Instance;
  index: number;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onToggleDropdown: (index: number) => void;
}

// Helper function to format price values
const formatPrice = (value: number | string): string => {
  return typeof value === "number" ? `$${value.toFixed(4)}` : value;
};

const calculatePercentageDiff = (
  basePrice: number | string,
  comparePrice: number | string
): number | null => {
  const base = typeof basePrice === "number" ? basePrice : parseFloat(String(basePrice));
  const compare = typeof comparePrice === "number" ? comparePrice : parseFloat(String(comparePrice));

  if (isNaN(base) || isNaN(compare) || base === 0) return null;

  return ((compare - base) / base) * 100;
};

const formatPercentageDiff = (diff: number | null): React.ReactNode => {
  if (diff === null) return null;

  const isNegative = diff < 0;
  const absDiff = Math.abs(diff);
  const colorClass = isNegative ? "text-green-600" : "text-red-600";
  const Icon = isNegative ? ArrowDown : ArrowUp;
  const sign = isNegative ? "" : "+";

  return (
    <div className={`flex items-center gap-1 text-xs mt-1 ${colorClass}`}>
      <Icon size={12} />
      <span>{sign}{absDiff.toFixed(1)}%</span>
    </div>
  );
};

const COLUMN_ORDER: ColumnKey[] = [
  "instanceType",
  "instanceFamily",
  "vcpus",
  "memory",
  "cpuArchitecture",
  "gpu",
  "gpuName",
  "aws",
  "gcp",
  "azure",
  "best",
];

// Cell renderer configuration
type CellRenderer = (instance: Instance, key: ColumnKey, basePrice?: number | string | null, providerType?: string) => React.ReactNode;

const CELL_RENDERERS: Record<ColumnKey, CellRenderer> = {
  instanceType: (instance, key) => (
    <td key={key} className="px-6 py-4 font-medium text-gray-900">
      {instance.instanceType}
    </td>
  ),
  instanceFamily: (instance, key) => (
    <td key={key} className="px-6 py-4 text-gray-700">
      {instance.instanceFamily}
    </td>
  ),
  vcpus: (instance, key) => (
    <td key={key} className="px-6 py-4 text-gray-700">
      {instance.vcpus}
    </td>
  ),
  memory: (instance, key) => (
    <td key={key} className="px-6 py-4 text-gray-700">
      {instance.memory}
    </td>
  ),
  cpuArchitecture: (instance, key) => (
    <td key={key} className="px-6 py-4 text-gray-700">
      {instance.cpuArchitecture}
    </td>
  ),
  gpu: (instance, key) => (
    <td key={key} className="px-6 py-4 text-gray-700">
      {instance.hasGpu ? (
        typeof instance.gpu === "number"
          ? `${instance.gpu.toFixed(2)} GB`
          : instance.gpu !== "-"
            ? `${instance.gpu} GB`
            : "-"
      ) : (
        "-"
      )}
    </td>
  ),
  gpuName: (instance, key) => (
    <td key={key} className="px-6 py-4 text-gray-700">
      {instance.hasGpu && instance.gpuName ? instance.gpuName : "-"}
    </td>
  ),
  aws: (instance, key, basePrice, providerType) => (
    <td key={key} className="px-6 py-4 text-gray-700">
      <div className="flex flex-col">
        <span>{formatPrice(instance.aws)}</span>
        {providerType !== "aws" && basePrice && formatPercentageDiff(
          calculatePercentageDiff(basePrice, instance.aws)
        )}
      </div>
    </td>
  ),
  gcp: (instance, key, basePrice, providerType) => (
    <td key={key} className="px-6 py-4 text-gray-700">
      <div className="flex flex-col">
        <span>{formatPrice(instance.gcp)}</span>
        {providerType !== "gcp" && basePrice && formatPercentageDiff(
          calculatePercentageDiff(basePrice, instance.gcp)
        )}
      </div>
    </td>
  ),
  azure: (instance, key, basePrice, providerType) => (
    <td key={key} className="px-6 py-4 text-gray-700">
      <div className="flex flex-col">
        <span>{formatPrice(instance.azure)}</span>
        {providerType !== "azure" && basePrice && formatPercentageDiff(
          calculatePercentageDiff(basePrice, instance.azure)
        )}
      </div>
    </td>
  ),
  best: (instance, key) => (
    <td key={key} className="px-6 py-4">
      <BestBadge best={instance.best} />
    </td>
  ),
};

const InstanceRow: React.FC<InstanceRowProps> = ({
  instance,
  index,
  isSelected,
  onSelect,
  onToggleDropdown,
}) => {
  const columnVisibility = useFilterStore((state) => state.columnVisibility);
  const providerType = useInstanceStore((state) => state.providerType);

  const getBasePrice = (): number | string | null => {
    const price = providerType === "aws" ? instance.aws : providerType === "gcp" ? instance.gcp : instance.azure;
    if (price === "-" || price === null || price === undefined) return null;
    const numPrice = typeof price === "number" ? price : parseFloat(String(price));
    return isNaN(numPrice) ? null : price;
  };

  const basePrice = getBasePrice();

  // Memoize visible columns to avoid recalculation on every render
  const visibleColumns = useMemo(
    () => COLUMN_ORDER.filter((key) => columnVisibility[key]),
    [columnVisibility]
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      const target = e.target as HTMLElement;
      if (target.tagName.toLowerCase() === "input") return;
      onToggleDropdown(index);
    },
    [index, onToggleDropdown]
  );

  const handleChevronClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      e.stopPropagation();
      onToggleDropdown(index);
    },
    [index, onToggleDropdown]
  );

  return (
    <tr
      className="hover:bg-gray-50 transition-colors relative cursor-pointer"
      onClick={handleRowClick}
    >
      {/* Checkbox - always visible */}
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(instance.id)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-default"
        />
      </td>

      {/* Render visible columns */}
      {visibleColumns.map((key) => {
        if (key === "aws" || key === "gcp" || key === "azure") {
          return CELL_RENDERERS[key](instance, key, basePrice, providerType);
        }
        return CELL_RENDERERS[key](instance, key);
      })}

      {/* Actions - always visible */}
      <td className="px-6 py-4 relative">
        <ChevronDown
          className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600"
          onClick={handleChevronClick}
        />
      </td>
    </tr>
  );
};

export default InstanceRow;
